/**
 * Todo Description Component
 * Displays todo description with expand/collapse functionality
 * Strips frontmatter block (---\n...\n---) since those values are shown in metadata grid above
 * Empty notes show a teaching empty state (not a blank void).
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { BasecampTodo } from '@/types/basecamp';
import { fixBasecampCommentHtml } from '../utils/comment-html';
import { useHydrateBasecampImages } from '../hooks/use-hydrate-basecamp-images';
import { BasecampCommentStyles } from '../styles/basecamp-comment-styles';
import { testIds } from '@/lib/test-ids';

/** Remove frontmatter block from description HTML - values are shown in metadata grid above.
 * Handles both <pre>---\n...\n---</pre> and sequences of <div>---</div>...<div>---</div> */
function stripFrontmatterBlock(html: string): string {
  // Strip <pre> wrapped frontmatter
  let result = html.replace(/<pre[^>]*>---\s*\n[\s\S]*?\n---\s*<\/pre>\s*/i, '');
  // Strip <div> wrapped frontmatter: from first <div>---</div> to closing <div>---</div>
  result = result.replace(/(<div[^>]*>-{3}<\/div>)[\s\S]*?(<div[^>]*>-{3}<\/div>)\s*/i, '');
  return result.trim();
}

interface TodoDescriptionProps {
  todo: BasecampTodo;
  /** True in the wide/main-panel layout, where there's plenty of vertical room to just let the
   * panel scroll - false in the narrow/stacked layout, where the collapse/"Show more" clamp
   * still applies. See basecamp-todo-viewer.tsx's `isWideDescriptionLayout`. Defaults to false
   * (collapsed/clamped) so callers that don't pass it keep the original behavior. */
  isWideLayout?: boolean;
  /** Optional Pull action when notes are empty (explicit sync - Rule 5). */
  onPull?: () => void;
  projectId?: string;
}

export function TodoDescription({
  todo,
  isWideLayout = false,
  onPull,
}: TodoDescriptionProps) {
  const [descriptionExpanded, setDescriptionExpanded] = useState(isWideLayout);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  // Keep expanded when the grid is 2-col; clamp only in the stacked/narrow layout.
  useEffect(() => {
    if (isWideLayout) {
      setDescriptionExpanded(true);
    }
  }, [isWideLayout]);

  // Basecamp API: `description` = HTML notes, `content` = todo title (NEVER use content here)
  const todoAny = todo as any;
  const rawDescription = todoAny.description || todoAny.notes || '';
  // Strip frontmatter block - those values are shown in TodoMetadataGrid above
  const strippedContent = stripFrontmatterBlock(rawDescription);
  // Repair Basecamp's rich-text HTML (mentions nested in <p> get displaced by the
  // browser's HTML parser - see comment-html.ts) before it is ever rendered.
  const rawContent = useMemo(() => fixBasecampCommentHtml(strippedContent), [strippedContent]);
  useHydrateBasecampImages(descriptionRef, rawContent);

  const hasContent = rawContent.length > 0;

  // Clamp/"Show more" only matters in the narrow layout - skip the scrollHeight measurement
  // entirely in the wide layout so it never clamps mid-transition while resizing.
  useEffect(() => {
    if (isWideLayout || !descriptionRef.current) {
      setShowExpandButton(false);
      return;
    }

    const checkHeight = () => {
      if (descriptionRef.current) {
        setShowExpandButton(descriptionRef.current.scrollHeight > 200);
      }
    };

    // Check immediately and after a short delay (images/content may not be loaded yet)
    checkHeight();
    const timer = setTimeout(checkHeight, 300);

    // Also re-check when images load
    const images = descriptionRef.current.querySelectorAll('img');
    images.forEach(img => img.addEventListener('load', checkHeight));

    return () => {
      clearTimeout(timer);
      images.forEach(img => img.removeEventListener('load', checkHeight));
    };
  }, [rawContent, isWideLayout]);

  // Teaching empty state - never leave a blank void when notes are missing.
  // Do not add "Open in Basecamp" here - chrome/footer already has that affordance.
  if (!hasContent) {
    return (
      <div className="space-y-2" data-testid={testIds.basecamp.todoEmptyNotes}>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Description
        </h2>
        <div className="max-w-md space-y-3 border-t border-border pt-4">
          <p className="text-sm font-medium">No notes on this todo</p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Basecamp description is empty, or only YAML frontmatter (shown in metadata above).
            Add notes in Basecamp, or Pull after someone updates them.
          </p>
          {onPull ? (
            <Button size="sm" variant="outline" onClick={onPull}>
              <RefreshCw className="mr-1 h-3.5 w-3.5" />
              Pull from Basecamp
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  const showCollapseUi = !isWideLayout && showExpandButton;

  return (
    <div className="space-y-2">
      <BasecampCommentStyles />
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Description
        </h2>
        {showCollapseUi && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDescriptionExpanded(!descriptionExpanded)}
            className="text-xs h-7 gap-1"
          >
            {descriptionExpanded ? (
              <>
                <ChevronUp className="h-3 w-3" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" />
                Show more
              </>
            )}
          </Button>
        )}
      </div>
      <div className="relative">
        <div
          ref={descriptionRef}
          className={`basecamp-todo-description prose prose-sm dark:prose-invert max-w-none prose-p:text-foreground dark:prose-p:text-foreground prose-p:opacity-90 dark:prose-p:opacity-100 prose-p:leading-relaxed prose-headings:text-foreground dark:prose-headings:text-foreground prose-ul:text-foreground prose-ol:text-foreground prose-li:text-foreground overflow-hidden transition-all duration-200 [&_h1]:mt-3 [&_h1]:mb-1 [&_h1]:text-sm [&_h1]:font-semibold [&_h1]:leading-snug first:[&_h1]:mt-0 [&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:leading-snug first:[&_h2]:mt-0 [&_h3]:mt-2.5 [&_h3]:mb-1 [&_h3]:text-[13px] [&_h3]:font-semibold [&_h3]:leading-snug [&_h4]:mt-2 [&_h4]:mb-0.5 [&_h4]:text-xs [&_h4]:font-semibold [&_h5]:mt-2 [&_h5]:mb-0.5 [&_h5]:text-xs [&_h5]:font-semibold [&_h6]:mt-2 [&_h6]:mb-0.5 [&_h6]:text-xs [&_h6]:font-semibold ${
            showCollapseUi && !descriptionExpanded ? 'max-h-[200px]' : ''
          }`}
          dangerouslySetInnerHTML={{ __html: rawContent }}
        />
        {showCollapseUi && !descriptionExpanded && (
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent pointer-events-none" />
        )}
      </div>
    </div>
  );
}

